/**
 * BF2 kategoriflätad kollektionsordning.
 *
 * Sektion: sections/bf2-main-collection.liquid (rutnätet med
 * data-bf2-order-collection, -section, -per-page och -total).
 *
 * På en blandad kollektion som /collections/sovrum ligger produkterna i
 * serverns ordning, vilket i praktiken betyder att alla sängar kommer först
 * och tapeterna hamnar långt ned. ORDER nedan beskriver i stället en önskad
 * kategisekvens, och modulen väver om rutnätet så att varje kategori får två
 * rader i taget innan nästa tar vid. Allt som inte matchar någon kategori
 * hamnar i en sista hink och vävs in på samma sätt.
 *
 * Antalen per kategori läses ur filtrets facettetiketter ("Sängar (12)"),
 * så inget behöver dubbellagras. Korten hämtas via Section Rendering API,
 * ett anrop per kategori och sida som sidan faktiskt behöver.
 *
 * Modulen backar och lämnar serverns ordning i fred om kunden filtrerar
 * eller sorterar själv, om facettantalen saknas, eller om för få kort kom
 * tillbaka för att vävningen ska bli meningsfull.
 */

import { createDebugger } from '~/scripts/utils/debug.js';

const debug = createDebugger('bf2-collection-order', false);

const GRID = '.bf2-products__grid--collection';

const MF = (key) => `filter.p.m.custom.${key}`;
const MOBEL = (value) => ({ key: 'mobelkategori', value });
const HUVUD = (value) => ({ key: 'huvudkategori', value });

/** Önskad kategisekvens per kollektionshandtag. */
const ORDER = {
    sovrum: [
        MOBEL('Sängar'),
        MOBEL('Sängbord'),
        HUVUD('Tyger'),
        HUVUD('Belysning'),
        MOBEL('Byråer'),
        MOBEL('Fåtöljer'),
        MOBEL('Soffor'),
        HUVUD('Tapeter'),
        MOBEL('Puffar & ottomaner'),
    ],
};

/** Två rader i taget, oavsett hur många kolumner rutnätet råkar ha. */
function blockSize(grid) {
    const cols = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
    return Math.max(1, cols) * 2;
}

/** Läser "(12)" ur filtrets etiketter till { "mobelkategori:Sängar": 12 }. */
function readFacetCounts() {
    const counts = {};

    document.querySelectorAll('input[name^="filter.p.m.custom."]').forEach((input) => {
        const key = input.name.replace('filter.p.m.custom.', '');
        const label = input.closest('label') || document.querySelector(`label[for="${input.id}"]`);
        const match = label && label.textContent.match(/\((\d[\d\s]*)\)\s*$/);

        if (match) {
            counts[`${key}:${input.value}`] = parseInt(match[1].replace(/\s/g, ''), 10);
        }
    });

    return counts;
}

/**
 * Bygger en platt lista över vilken hink varje position i rutnätet ska ta
 * sitt kort ifrån, block för block tills alla hinkar är tömda.
 */
function buildPlan(order, counts, totalProducts, block) {
    const buckets = order
        .map((entry) => ({ ...entry, total: counts[`${entry.key}:${entry.value}`] || 0, taken: 0 }))
        .filter((bucket) => bucket.total > 0);

    const named = buckets.reduce((sum, bucket) => sum + bucket.total, 0);

    // Resten: produkter som inte matchar någon av kategorierna ovan.
    buckets.push({ key: null, value: null, total: Math.max(0, totalProducts - named), taken: 0 });

    const plan = [];
    let progressed = true;

    while (progressed) {
        progressed = false;

        for (const bucket of buckets) {
            const remaining = bucket.total - bucket.taken;
            if (remaining <= 0) continue;

            const take = Math.min(block, remaining);
            for (let i = 0; i < take; i += 1) {
                plan.push({ bucket, index: bucket.taken + i });
            }

            bucket.taken += take;
            progressed = true;
        }
    }

    return plan;
}

/** Hämtar en sida kort för en hink via Section Rendering API. */
async function fetchCards(handle, sectionId, bucket, page, perPage) {
    const params = new URLSearchParams({ section_id: sectionId });

    if (bucket.key) params.set(MF(bucket.key), bucket.value);
    if (page > 1) params.set('page', String(page));

    const response = await fetch(`/collections/${handle}?${params}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) {
        throw new Error(`${response.status} for ${bucket.value || 'ovrigt'}`);
    }

    const grid = new DOMParser()
        .parseFromString(await response.text(), 'text/html')
        .querySelector(GRID);

    const cards = grid ? Array.from(grid.children) : [];
    debug.log('hamtade', cards.length, bucket.value || 'ovrigt', 'sida', page, 'av', perPage);

    return cards;
}

function handleOf(card) {
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    return link.getAttribute('href').split('/products/')[1].split(/[?#]/)[0];
}

async function reorder(grid) {
    const handle = grid.dataset.bf2OrderCollection;
    const sectionId = grid.dataset.bf2OrderSection;
    const perPage = parseInt(grid.dataset.bf2OrderPerPage, 10) || 28;
    const total = parseInt(grid.dataset.bf2OrderTotal, 10) || 0;
    const order = ORDER[handle];

    if (!order || !total) return;

    // Filtrerar eller sorterar kunden själv är serverns ordning den rätta.
    const params = new URLSearchParams(location.search);
    for (const name of params.keys()) {
        if (name.startsWith('filter.') || name === 'sort_by') return;
    }

    const counts = readFacetCounts();
    if (!Object.keys(counts).length) {
        debug.warn('inga facettantal i filtret, behaller serverns ordning');
        return;
    }

    const page = Math.max(1, parseInt(params.get('page'), 10) || 1);
    const slice = buildPlan(order, counts, total, blockSize(grid)).slice(
        (page - 1) * perPage,
        page * perPage,
    );

    if (!slice.length) return;

    // Ett anrop per (hink, sida) som den här sidan faktiskt rör vid.
    const needed = new Map();
    for (const item of slice) {
        const wanted = Math.floor(item.index / perPage) + 1;
        const key = `${item.bucket.key || 'rest'}:${item.bucket.value || ''}:${wanted}`;
        if (!needed.has(key)) needed.set(key, { bucket: item.bucket, page: wanted });
    }

    const fetched = new Map();
    await Promise.all(
        Array.from(needed.entries()).map(async ([key, job]) => {
            fetched.set(key, await fetchCards(handle, sectionId, job.bucket, job.page, perPage));
        }),
    );

    const placed = new Set();
    const cards = [];

    for (const item of slice) {
        const wanted = Math.floor(item.index / perPage) + 1;
        const key = `${item.bucket.key || 'rest'}:${item.bucket.value || ''}:${wanted}`;
        const card = (fetched.get(key) || [])[item.index % perPage];
        if (!card) continue;

        // En produkt kan matcha flera kategorier. Först till kvarn.
        const productHandle = handleOf(card);
        if (productHandle && placed.has(productHandle)) continue;
        if (productHandle) placed.add(productHandle);

        cards.push(card);
    }

    if (cards.length < slice.length / 2) {
        debug.warn('for fa kort vavdes fram, behaller serverns ordning');
        return;
    }

    grid.replaceChildren(...cards);
    grid.dataset.bf2OrderApplied = 'true';
    debug.log('vavde', cards.length, 'kort pa sida', page);
}

function init() {
    document.querySelectorAll(`${GRID}[data-bf2-order-collection]`).forEach((grid) => {
        if (grid.dataset.bf2OrderApplied) return;
        reorder(grid).catch((e) => debug.warn('kunde inte vava ordningen', e));
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

// Kollektionens AJAX-filter byter ut hela innehållet, så kör om efter varje
// omrendering. init() självguardar på data-bf2-order-applied.
const ajaxRoot = document.getElementById('CollectionAjaxContent');
if (ajaxRoot) {
    new MutationObserver(() => init()).observe(ajaxRoot.parentNode || ajaxRoot, {
        childList: true,
        subtree: true,
    });
}
